/**
 * Platform-specific Native Interface
 *
 * Android/iOS 플랫폼별 네이티브 호출 로직
 */

import { apiMocks } from '@/api/bridge/api/mocks'
import { appMocks } from '@/api/bridge/app/mocks'
import { deviceMocks } from '@/api/bridge/device/mocks'
import { navigationMocks } from '@/api/bridge/navigation/mocks'
import { uiMocks } from '@/api/bridge/ui/mocks'
import { userMocks } from '@/api/bridge/user/mocks'

import { BridgeError, logBridgeError } from './errors'
import { getMockOverride, isMockOverrideEnabled } from './mockStorage'
import type { BridgeMessage } from './types'

export type PlatformType = 'android' | 'ios' | 'web' | 'unknown'

/**
 * 현재 플랫폼 감지
 */
export function detectPlatform(): PlatformType {
  if (typeof window === 'undefined') {
    return 'unknown'
  }

  // Android 감지 - window.Android.receiveMessage 우선 체크
  if ((window as any).Android?.receiveMessage) {
    return 'android'
  }

  // iOS 감지
  if ((window as any).webkit?.messageHandlers?.app) {
    return 'ios'
  }

  // Web 환경 (개발 환경 또는 네이티브 인터페이스가 없는 경우)
  return 'web'
}

/**
 * 플랫폼별 네이티브 메시지 전송
 */
export function sendToNative(message: BridgeMessage): void {
  const platform = detectPlatform()

  try {
    switch (platform) {
      case 'android':
        sendToAndroid(message)
        break
      case 'ios':
        sendToIOS(message)
        break
      case 'web':
        // 개발 환경에서의 fallback
        sendToWebFallback(message)
        break
      default:
        throw BridgeError.platformNotSupported()
    }
  } catch (error) {
    logBridgeError(error as BridgeError, 'sendToNative')
    throw error
  }
}

/**
 * Android 플랫폼으로 메시지 전송
 */
function sendToAndroid(message: BridgeMessage): void {
  // 최신 Android 인터페이스 우선 사용
  const android = (window as any).Android
  if (android?.receiveMessage) {
    try {
      // Android는 JSON 문자열로 전송
      android.receiveMessage(JSON.stringify(message))

      return
    } catch (error) {
      throw new BridgeError(
        'NATIVE_ERROR',
        `Android receiveMessage failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        undefined,
        message.requestId
      )
    }
  }

  // 기존 SharedWebInterface 호환 (fallback)
  const sharedWeb = (window as any).SharedWeb
  if (sharedWeb?.sendMessage) {
    try {
      // Android는 JSON 문자열로 전송
      sharedWeb.sendMessage(JSON.stringify(message))

      return
    } catch (error) {
      throw new BridgeError(
        'NATIVE_ERROR',
        `Android sendMessage failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        undefined,
        message.requestId
      )
    }
  }

  // 둘 다 없으면 에러
  throw BridgeError.platformNotSupported()
}

/**
 * iOS 플랫폼으로 메시지 전송
 */
function sendToIOS(message: BridgeMessage): void {
  const webkit = (window as any).webkit
  if (!webkit?.messageHandlers?.app?.postMessage) {
    throw BridgeError.platformNotSupported()
  }

  try {
    // iOS는 객체로 직접 전송
    webkit.messageHandlers.app.postMessage(message)
  } catch (error) {
    throw new BridgeError(
      'NATIVE_ERROR',
      `iOS postMessage failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
      undefined,
      message.requestId
    )
  }
}

/**
 * Web 환경 fallback (개발/스테이지 환경용)
 * localStorage 오버라이드가 활성화되어 있거나 개발 환경이면 모의 응답 생성
 */
function sendToWebFallback(message: BridgeMessage): void {
  const isDev = process.env.SERVICE_ENV === 'development'
  const shouldMock = process.env.NODE_ENV === 'development' || isDev || isMockOverrideEnabled()

  if (shouldMock) {
    // 모의 응답을 위한 이벤트 발생 (테스트용)
    setTimeout(() => {
      const mockResponse = {
        requestId: message.requestId,
        group: message.group,
        action: message.action,
        status: 'SUCCESS' as const,
        data: {
          ...getMockResponseData(message.group, message.action),
          _mockResponse: true, // 모의 응답임을 표시
          _mockTimestamp: new Date().toISOString() // 모의 응답 생성 시간
        }
      }

      if (window.appMessageHandler) {
        window.appMessageHandler(mockResponse)
      }
    }, 100) // 100ms 지연으로 실제 네이티브 응답 시뮬레이션
  }
}

/**
 * 개발 환경용 모의 응답 데이터 생성
 * localStorage에 사용자 정의 오버라이드가 있으면 우선 사용
 */
function getMockResponseData(group: string, action: string): any {
  // localStorage 오버라이드가 활성화되어 있으면 우선 사용
  if (isMockOverrideEnabled()) {
    const override = getMockOverride(group, action)
    if (override !== null) {
      return override
    }
  }

  const mocksMap: Record<string, Record<string, any>> = {
    App: appMocks,
    User: userMocks,
    Device: deviceMocks,
    Ui: uiMocks,
    Navigation: navigationMocks,
    Api: apiMocks
  }

  return mocksMap[group]?.[action] || {}
}

/**
 * 네이티브 인터페이스 사용 가능 여부 확인
 */
export function isNativeInterfaceAvailable(): boolean {
  const platform = detectPlatform()

  return platform === 'android' || platform === 'ios'
}

/**
 * 플랫폼 정보 로깅 (디버깅용)
 */
export function logPlatformInfo(): void {
  if (process.env.NODE_ENV === 'development') {
    const platform = detectPlatform()
    console.log('[Bridge] Platform Info:', {
      platform,
      isNativeAvailable: isNativeInterfaceAvailable(),
      hasAndroid: !!(window as any).Android?.receiveMessage,
      hasSharedWeb: !!(window as any).SharedWeb?.sendMessage,
      hasWebkit: !!(window as any).webkit?.messageHandlers?.app
    })
  }
}
